import { Link, Outlet, useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Eye } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { usePreviewGuard } from "@/lib/preview";
import { BrandLockup } from "@/components/Brand";
import { Button } from "@/components/ui/button";

const linkCls = "sidebar-link inline-flex shrink-0 items-center gap-2 rounded-md px-2.5 py-2 text-xs font-semibold uppercase tracking-[0.12em]";

const PreviewBanner = ({ name, onExit }) => (
  <div className="sticky top-0 z-30 border-b border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.18)]" data-testid="preview-banner">
    <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2.5 sm:px-6">
      <div className="flex min-w-0 items-center gap-2 text-sm font-semibold">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="min-w-0" data-testid="preview-banner-text">Previewing as {name} — read-only</span>
      </div>
      <Button size="sm" variant="outline" className="bg-card" onClick={onExit} data-testid="exit-preview-button">Exit preview</Button>
    </div>
  </div>
);

export default function PortalLayout() {
  const { user, logout, exitPreview } = useApp();
  const { preview, guard } = usePreviewGuard();
  const navigate = useNavigate();
  const exit = () => { exitPreview(); navigate("/admin"); };
  const name = preview ? preview.name : user?.name;
  return (
    <div className="min-h-screen" data-testid="portal-layout">
      {preview && <PreviewBanner name={preview.name} onExit={exit} />}
      <header className="sidebar-surface grain relative border-b">
        <div className="relative mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link to="/portal" data-testid="portal-home-link"><BrandLockup compact /></Link>
          <div className="flex items-center gap-1">
            <Link to="/portal/password" onClick={guard()} data-testid="change-password-link" className={linkCls}>
              <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Password</span>
            </Link>
            {!preview && (
              <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className={linkCls}>
                <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-1 text-sm text-muted-foreground" data-testid="portal-greeting">Welcome back, {name?.split(" ")[0]}</div>
        <Outlet />
      </main>
    </div>
  );
}
